/**
 * Auth Provider for 1DA Platform
 *
 * セキュアな認証管理:
 * - localStorageを使用しない（セキュリティリスク回避）
 * - 毎回バックエンドで認証状態を検証
 * - HttpOnly Cookieでセッション管理
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { apolloClient } from '@/graphql/client'
import {
  useGetMeQuery,
  useLoginMutation,
  useLogoutMutation,
  type AuthUser,
} from '@/graphql/generated/operations'

// Types - AuthUserを拡張
type User = Pick<
  AuthUser,
  | 'id'
  | 'email'
  | 'username'
  | 'displayName'
  | 'isActive'
  | 'isSuperuser'
  | 'organizationId'
  | 'organizationRole'
  | 'organizationName'
>

interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isAdmin: () => boolean
  refetchUser: () => Promise<void>
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const pathname = location.pathname
  const [user, setUser] = useState<User | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Skip auth check on public pages
  const isPublicPage = pathname === '/login' || pathname === '/' || pathname === '/register'

  // Fetch current user - always verify with backend
  const { data, loading: meLoading, error: meError, refetch: refetchMe } = useGetMeQuery({
    skip: isPublicPage,
    fetchPolicy: 'network-only', // Always verify with backend, no cache
    onCompleted: (data) => {
      if (import.meta.env.DEV) {
        console.log('[AuthProvider] ME_QUERY completed:', data?.me ? 'User found' : 'No user')
      }
      if (data?.me) {
        setUser({
          id: data.me.id,
          username: data.me.username,
          email: data.me.email,
          displayName: data.me.displayName,
          isActive: data.me.isActive,
          isSuperuser: data.me.isSuperuser,
          organizationId: data.me.organizationId,
          organizationRole: data.me.organizationRole,
          organizationName: data.me.organizationName,
        })
        setError(null)
      } else {
        setUser(null)
      }
    },
    onError: (error) => {
      if (import.meta.env.DEV) {
        console.log('[AuthProvider] ME_QUERY error:', error.message)
      }
      // セッション切れの場合はCSRFトークンもクリア
      localStorage.removeItem('csrfToken')
      setUser(null)
      setError(null) // Don't show error for unauthenticated state
    },
  })

  // Redirect to login if not authenticated (except on public pages)
  useEffect(() => {
    if (isPublicPage) return

    // 認証エラーまたはユーザー情報がない場合はログイン画面にリダイレクト
    const shouldRedirect = !meLoading && (meError || (data !== undefined && !data?.me))
    if (shouldRedirect) {
      if (import.meta.env.DEV) {
        console.log('[AuthProvider] Redirecting to /login - user not authenticated', { error: !!meError, data: !!data?.me })
      }
      navigate('/login', { replace: true })
    }
  }, [meLoading, data, meError, pathname, navigate, isPublicPage])

  // Login mutation
  const [loginMutation, { loading: loginLoading }] = useLoginMutation({
    onCompleted: (data) => {
      if (import.meta.env.DEV) {
        console.log('[Auth] Login response:', data?.login?.success ? 'Success' : 'Failed')
      }

      if (data?.login?.success && data.login.user) {
        // CSRFトークンをlocalStorageに保存（client.tsがここから読み取る）
        if (data.login.csrfToken) {
          localStorage.setItem('csrfToken', data.login.csrfToken)
          if (import.meta.env.DEV) {
            console.log('[Auth] CSRF token saved to localStorage')
          }
        }

        setUser({
          id: data.login.user.id,
          email: data.login.user.email,
          username: data.login.user.username || data.login.user.email,
          displayName: data.login.user.displayName || data.login.user.username || data.login.user.email,
          isActive: data.login.user.isActive ?? true,
          isSuperuser: data.login.user.isSuperuser ?? false,
          organizationId: data.login.user.organizationId,
          organizationRole: data.login.user.organizationRole,
          organizationName: data.login.user.organizationName,
        })
        setError(null)
        navigate('/dashboard')
      } else {
        setError(data?.login?.message || 'ログインに失敗しました')
      }
    },
    onError: (error) => {
      console.error('Login error:', error)
      setError(error.message || 'ログインに失敗しました')
    },
  })

  // Logout mutation
  const [logoutMutation, { loading: logoutLoading }] = useLogoutMutation({
    onCompleted: (data) => {
      if (data?.logout?.success) {
        // CSRFトークンをクリア
        localStorage.removeItem('csrfToken')
        setUser(null)
        setError(null)
        apolloClient.clearStore()
        navigate('/')
      }
    },
    onError: (error) => {
      console.error('Logout error:', error)
      // Even on error, clear local state and redirect
      localStorage.removeItem('csrfToken')
      setUser(null)
      apolloClient.clearStore()
      navigate('/')
    },
  })

  // Login function
  const login = async (email: string, password: string) => {
    setError(null)
    try {
      await loginMutation({
        variables: {
          input: {
            email,
            password,
          },
        },
      })
    } catch (error) {
      throw error
    }
  }

  // Logout function
  const logout = async () => {
    setError(null)
    try {
      await logoutMutation()
    } catch (error) {
      throw error
    }
  }

  // Refetch user
  const refetchUser = async () => {
    try {
      await refetchMe()
    } catch (error) {
      console.error('[AuthProvider] Error refetching user:', error)
    }
  }

  // Check if user is admin
  const isAdmin = () => {
    return user?.isSuperuser || false
  }

  const isLoading = meLoading || loginLoading || logoutLoading

  // Show loading spinner while checking auth (except on public pages)
  if (!isPublicPage && meLoading && !data && !user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">認証状態を確認中...</p>
        </div>
      </div>
    )
  }

  // Don't render children if not authenticated (except on public pages)
  if (!isPublicPage && !meLoading && !user) {
    return null
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: isLoading,
        error,
        login,
        logout,
        isAdmin,
        refetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Protected route wrapper component
export function ProtectedRoute({
  children,
  requireAdmin = false,
}: {
  children: ReactNode
  requireAdmin?: boolean
}) {
  const { user, loading, isAdmin } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname)}`} replace />
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/unauthorized" replace />
  }

  return <>{children}</>
}

// Public route wrapper (redirect to dashboard if authenticated)
export function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
